<template>
  <a-tooltip
    :content="tooltipDisplayContent"
    :popup-visible="isOverflowing ? undefined : false"
    v-bind="$attrs"
  >
    <div ref="containerRef" class="auto-tooltip-container">
      <slot />
    </div>
  </a-tooltip>
</template>

<script setup lang="ts">
  import { ref, onMounted, onBeforeUnmount } from 'vue';

  const props = defineProps({
    /**
     * @zh tooltip的内容。如果未提供，则默认为插槽内的文本。
     * @en The content of the tooltip. If not provided, it defaults to the text within the slot.
     */
    content: {
      type: String,
    },
  });

  defineOptions({
    name: 'AutoTooltip',
    inheritAttrs: false,
  });

  const containerRef = ref<HTMLElement | null>(null);
  const tooltipDisplayContent = ref<string | undefined>(undefined);
  const isOverflowing = ref(false);
  let resizeObserver: ResizeObserver | null = null;

  const checkOverflow = () => {
    const container = containerRef.value;
    if (container) {
      // 在单层结构中，直接比较同一个元素的 scrollWidth 和 clientWidth
      isOverflowing.value = container.scrollWidth > container.clientWidth;
      if (isOverflowing.value) {
        tooltipDisplayContent.value = props.content ?? container.innerText;
      } else {
        tooltipDisplayContent.value = undefined;
      }
    }
  };

  onMounted(() => {
    if (containerRef.value) {
      checkOverflow();
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(containerRef.value);
    }
  });

  onBeforeUnmount(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
  });
</script>

<style scoped>
  .auto-tooltip-container {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  }
</style>
